import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Store, X } from 'lucide-react';

/**
 * 发品/同步等写操作前选择具体店铺。
 */
export default function PlatformShopPickModal({
  open,
  shops = [],
  title = '选择店铺',
  description = '请选择要执行操作的店铺（仅对选中店铺调用平台接口）',
  confirmText = '确认',
  initialShopId = '',
  onConfirm,
  onClose,
}) {
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setKeyword('');
    const initial = String(initialShopId || '').trim();
    const exists = (shops || []).some((s) => String(s.id) === initial);
    setSelectedId(exists ? initial : (shops?.[0] ? String(shops[0].id) : ''));
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, initialShopId, shops]);

  const filtered = useMemo(() => {
    const list = Array.isArray(shops) ? shops : [];
    const q = String(keyword || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((shop) => {
      const name = String(shop.shopName || '').toLowerCase();
      const id = String(shop.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [shops, keyword]);

  if (!open) return null;

  return createPortal(
    <div className="shein-shop-switch-modal" role="presentation" onClick={onClose}>
      <div
        className="shein-shop-switch-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-shop-pick-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shein-shop-switch-modal__head">
          <div>
            <h2 id="platform-shop-pick-title">{title}</h2>
            <p>{description}</p>
          </div>
          <button
            type="button"
            className="shein-shop-switch-modal__close"
            onClick={onClose}
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </header>

        <div className="shein-shop-switch-modal__toolbar">
          <div className="shein-shop-switch-modal__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              className="shein-shop-switch-modal__search-input"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索店铺名称"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <div className="shein-shop-switch-modal__list">
          {!shops.length ? (
            <div className="shein-shop-switch-modal__empty">
              <Store size={22} />
              <p>暂无可用店铺</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="shein-shop-switch-modal__empty">
              <p>没有匹配的店铺</p>
            </div>
          ) : (
            <table className="shein-shop-switch-modal__table">
              <thead>
                <tr>
                  <th scope="col">店铺名称</th>
                  <th scope="col">ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((shop) => {
                  const selected = String(shop.id) === String(selectedId);
                  return (
                    <tr
                      key={shop.id}
                      className={`shein-shop-switch-modal__row${selected ? ' is-active' : ''}`}
                      onClick={() => setSelectedId(String(shop.id))}
                      onDoubleClick={() => onConfirm?.(String(shop.id), shop)}
                    >
                      <td>
                        <span className="shein-shop-switch-modal__td-name">
                          <span className="shein-shop-switch-modal__name-text">
                            {shop.shopName || `店铺 ${shop.id}`}
                          </span>
                          {selected ? (
                            <span className="shein-shop-switch-modal__current-tag">已选</span>
                          ) : null}
                        </span>
                      </td>
                      <td>{shop.id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="shein-shop-switch-modal__toolbar" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={!selectedId}
            onClick={() => {
              const shop = (shops || []).find((s) => String(s.id) === String(selectedId));
              onConfirm?.(String(selectedId), shop);
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
